'use client';
import styled from 'styled-components';
import { ResponsiveImage } from '@web/molecules';

export const StyledTabbedShowcase = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing(16, 0)};
  background-color: ${props =>
    props.theme.colors.get(
      props.$backgroundColor,
      props.theme.colors.background.base,
    )};

  @media ${props => props.theme.laptop} {
    padding: ${props => props.theme.spacing(24, 0)};
  }
`;

export const SubHeadline = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.text.primary};

  p {
    ${props => props.theme.typography.largeBody};
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing(0, 6)};
  gap: ${props => props.theme.spacing(4, 0)};

  @media ${props => props.theme.laptop} {
    padding: ${props => props.theme.spacing(0, 12)};
  }
`;

export const StyledTabPanelsWrapper = styled.div`
  width: 100%;
  padding-top: ${props => props.theme.spacing(props.$oneTab ? 1 : 4)};
`;

export const TabBar = styled.div`
  position: relative;
  display: flex;
  justify-content: ${props => props.$alignTabs};
  gap: ${props => props.theme.spacing(6)};
  padding-bottom: 1px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${props => props.theme.tablet} {
    justify-content: center;
    gap: ${props => props.theme.spacing(14)};
  }

  @media ${props => props.theme.laptop} {
    gap: ${props => props.theme.spacing(20)};
  }
`;

export const Tab = styled.button`
  padding-bottom: ${props => props.theme.spacing(3)};
  ${props => props.theme.typography.button}
  font-weight: ${props => (props.$isActive ? 700 : 400)};
  letter-spacing: 0.01em;
  color: ${props =>
    props.$isActive
      ? props.theme.colors.get(
          props.$activeLabelColor,
          props.theme.colors.text.primary,
        )
      : props.theme.colors.get(
          props.$inactiveLabelColor,
          props.theme.colors.text.tertiary,
        )};
  background: none;
  border: none;
  cursor: pointer;
  transition: color 200ms ease;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid;
    outline-offset: 2px;
    border-radius: 2px;
  }

  @media ${props => props.theme.tablet} {
    font-size: 20px;
  }
`;

export const InkIndicator = styled.div`
  position: absolute;
  bottom: 0;
  height: 2.5px;
  background: ${props => props.theme.colors.secondary.plum};
  border-radius: 2px;
  pointer-events: none;
  transition: left 300ms cubic-bezier(0.4, 0, 0.2, 1),
    width 300ms cubic-bezier(0.4, 0, 0.2, 1);
`;

export const FullWidthDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${props => props.theme.colors.border.light};
`;

export const ImagePanel = styled.div`
  margin-top: ${props => props.theme.spacing(props.$oneTab ? 0 : 8)};
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 5 / 6;
  border-radius: ${props => props.theme.spacing(6)};
  box-shadow: 0 16px 56px rgba(44, 42, 38, 0.16);

  @media ${props => props.theme.tablet} {
    margin-top: ${props => props.theme.spacing(props.$oneTab ? 0 : 10)};
    aspect-ratio: 16 / 9;
  }
`;

export const PanelImage = styled(ResponsiveImage)`
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 380ms ease-in-out;
  object-position: ${props =>
    props.$mobileImageHorizontalPosition != null
      ? `${props.$mobileImageHorizontalPosition}%`
      : 'unset'};

  @media ${props => props.theme.tablet} {
    object-position: unset;
  }
`;

export const PanelOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 280ms ease;
  padding: ${props => props.theme.spacing(4)};

  @media ${props => props.theme.laptop} {
    padding: ${props => props.theme.spacing(8)};
  }
`;

export const BackgroundColorOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme, $backgroundColor }) =>
    theme.colors.get($backgroundColor, 'background.base')};
`;
export const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 8, 6, 0.78) 0%,
    rgba(10, 8, 6, 0.3) 45%,
    transparent 72%
  );
`;

export const PanelContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: ${props => props.theme.spacing(6)};
  width: 100%;
  opacity: ${props => (props.$fading ? 0 : 1)};
  transform: ${props => (props.$fading ? 'translateY(6px)' : 'translateY(0)')};
  transition: opacity 280ms ease, transform 280ms ease;

  @media ${props => props.theme.tablet} {
    max-width: 90%;
  }

  @media ${props => props.theme.laptop} {
    padding: ${props => props.theme.spacing(10)};
    max-width: 56%;
  }
`;

export const PanelHeadline = styled.h3`
  ${props =>
    props.theme.typography[props.$typography] ||
    props.theme.typography.heading2}
  color: ${props =>
    props.theme.colors.get(props.$fontColor, props.theme.colors.text.lightest)};

  @media ${props => props.theme.laptop} {
    margin-bottom: ${props => props.theme.spacing(2)};
  }
`;

export const PanelBody = styled.div`
  p {
    ${props => props.theme.typography.body}
    @media ${props => props.theme.laptop} {
      ${props => props.theme.typography.largeBody}
    }
    line-height: 20px;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing(5)};
`;
